import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Pill, ListChecks, IndianRupee, Loader2, CheckCircle2, RefreshCw, Plus, Trash2, Package, LogOut, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PharmacyReceipt from '../components/PharmacyReceipt';
import ReceptionLayout from '../components/ReceptionLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * Pharmacy LITE — quick "dispense + collect" flow tailored for small
 * clinics. The pharmacist sees every consultation that has a prescription
 * but no dispensing yet, picks one, optionally tweaks line items and
 * prices, then dispenses + collects payment in a single click.
 *
 * Full inventory / GRN / sales workflow lives in PharmacyPage.jsx (mode=
 * 'full'). The tenant admin toggles between the two via /admin/capabilities.
 */
export default function PharmacyLitePage() {
  const { user, logout } = useAuth();
  const [pending, setPending] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [tab, setTab] = useState('pending');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        ax.get('/api/clinic-ops/dispense/pending'),
        ax.get('/api/clinic-ops/dispense/completed').catch(() => ({ data: [] })),
      ]);
      setPending(p || []);
      setCompleted(c || []);
    } catch (_) {
      toast.error('Failed to load pharmacy orders');
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Wrap in ReceptionLayout for receptionist users so the sidebar nav
  // appears. Other roles (admin/pharmacist) keep the legacy compact
  // header to avoid changing their established workflow.
  const useLayout = (user?.role || '').toLowerCase() === 'receptiondesk' || (user?.role || '').toLowerCase() === 'admin';

  const body = (
    <>
      <main className={`${useLayout ? '' : 'max-w-7xl mx-auto'} p-6 grid grid-cols-1 lg:grid-cols-5 gap-6`}>
        {/* LEFT — Pending / Completed tabs */}
        <div className="lg:col-span-2 bg-white border rounded-xl p-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-3" data-testid="pl-tabs">
              <TabsTrigger value="pending" data-testid="pl-tab-pending">
                Pending · {pending.length}
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="pl-tab-completed">
                Completed · {completed.length}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pending">
              <div className="space-y-2 max-h-[75vh] overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-zinc-500">Loading…</p>
                ) : pending.length === 0 ? (
                  <div className="text-center py-12 text-zinc-400">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No prescriptions waiting.</p>
                  </div>
                ) : pending.map(p => (
                  <button key={p.consultation_id}
                    onClick={() => setSelected(p)}
                    className={`w-full text-left p-3 border rounded-lg transition ${
                      selected?.consultation_id === p.consultation_id
                        ? 'border-emerald-600 bg-emerald-50'
                        : 'hover:bg-zinc-50'
                    }`}
                    data-testid={`pl-pending-${p.consultation_id}`}>
                    <div className="font-medium text-sm text-zinc-900">{p.patient_name || '—'}</div>
                    <div className="text-[11px] text-zinc-500">
                      {(p.prescription || []).length} item(s)
                      {p.created_at ? ` · ${new Date(p.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="completed">
              <div className="space-y-2 max-h-[75vh] overflow-y-auto">
                {completed.length === 0 ? (
                  <div className="text-center py-12 text-zinc-400">
                    <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No completed orders yet.</p>
                  </div>
                ) : completed.map(c => (
                  <button key={c.id}
                    onClick={() => setReceipt(c)}
                    className="w-full text-left p-3 border rounded-lg hover:bg-zinc-50 transition"
                    data-testid={`pl-completed-${c.id}`}>
                    <div className="flex justify-between items-baseline">
                      <span className="font-medium text-sm text-zinc-900">{c.patient_name || '—'}</span>
                      <span className="font-bold text-sm text-emerald-700">₹{Number(c.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {(c.items || []).length} item(s) · {(c.payment_method || 'cash').toUpperCase()}
                      {c.created_at ? ` · ${new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT — dispense form (remounted per selection via key) */}
        <div className="lg:col-span-3 bg-white border rounded-xl p-5">
          {!selected ? (
            <div className="text-center py-24 text-zinc-400">
              <Pill className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Pick a prescription on the left to dispense.</p>
            </div>
          ) : (
            <DispenseForm
              key={selected.consultation_id}
              selected={selected}
              onCancel={() => setSelected(null)}
              onReceipt={(r) => setReceipt(r)}
              onDone={() => { setSelected(null); refresh(); }}
            />
          )}
        </div>
      </main>
      {receipt && <PharmacyReceipt data={receipt} onClose={() => setReceipt(null)} />}
    </>
  );

  if (useLayout) {
    return (
      <ReceptionLayout
        pageTitle="Pharmacy · Quick Dispense"
        pageSubtitle="Process prescriptions, collect payment with GST"
        rightActions={
          <Button variant="outline" size="sm" onClick={refresh} data-testid="pl-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        }
      >
        <div data-testid="pharmacy-lite-page">{body}</div>
      </ReceptionLayout>
    );
  }
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="pharmacy-lite-page">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-emerald-700" />
            <span className="font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Pharmacy · Quick Dispense
            </span>
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              LITE
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-600">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="pl-refresh-top">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="pl-logout">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      {body}
    </div>
  );
}

function DispenseForm({ selected, onCancel, onDone, onReceipt }) {
  // Seed from doctor's Rx. Preserve a snapshot so we can compute the
  // audit trail (original → final) when the pharmacist hits dispense.
  const seedRow = (p) => ({
    name: p.name || p.medicine || p.medicine_name || p.drug_name || p.item || '',
    qty: p.qty || p.quantity || p.dose_qty || 1,
    uom: p.uom || 'tab',
    price: p.price || p.mrp || 0,
    gst_percent: p.gst_percent ?? 12,
  });
  const seed = (selected.prescription || []).map(seedRow);
  const [items, setItems] = useState(seed.length ? seed : [{ name: '', qty: 1, uom: 'tab', price: 0, gst_percent: 12 }]);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [editReason, setEditReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inventory, setInventory] = useState(null);   // null = not fetched yet
  // Open suggestion dropdown index — only one row's suggestion box visible at a time
  const [suggestFor, setSuggestFor] = useState(null);
  // Whether we've already fused the doctor's Rx names with inventory
  // prices/GST/UOM. Fires once per selection (key= remounts this form).
  const [hydrated, setHydrated] = useState(false);

  // The original Rx snapshot used by the backend's audit diff. Captured
  // once per mount but UPDATED on hydrate so the auto-filled price/GST
  // isn't mistaken for a pharmacist edit (was: `const [x] = useState(...)`
  // which meant every hydrated row appeared "edited" from load).
  const [originalSnapshot, setOriginalSnapshot] = useState(() => seed.map(s => ({ ...s })));

  useEffect(() => {
    ax.get('/api/clinic-ops/inventory').then(({ data }) => setInventory(data || []))
      .catch(() => setInventory([]));
  }, []);

  // Build progressively broader catalog queries. The doctor's Rx uses
  // the format "generic (brand) strength" (e.g. "Glimepiride (Amaryl) 2 mg")
  // but the shared medicines catalog stores rows like "Amaryl 2mg Tablet"
  // or "Zolfresh 5 Tablet" — matching directly on the Rx text fails
  // because of the ")" + "mg" + "Tablet" tail. We try:
  //   1. brand + strength number  ("Amaryl 2", "Zolfresh 5")
  //   2. brand alone               ("Amaryl", "Zolfresh")
  //   3. sanitised full name       (parens stripped)
  // and stop at the first list that has hits — the closest match sits
  // at the top of the response.
  const buildCatalogQueries = (rxName) => {
    const s = (rxName || '').trim();
    if (!s) return [];
    const brandMatch = s.match(/\(([^)]+)\)/);
    const brand = brandMatch ? brandMatch[1].trim() : '';
    const strengthMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:mg|ml|mcg|g|iu)?/i);
    const strengthNum = strengthMatch ? strengthMatch[1] : '';
    const queries = [];
    if (brand && strengthNum) queries.push(`${brand} ${strengthNum}`);
    if (brand) queries.push(brand);
    const cleaned = s.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
    if (cleaned) queries.push(cleaned);
    // Dedupe preserving order
    return Array.from(new Set(queries));
  };

  // Fuse inventory (and global catalog fallback) into the item rows so
  // the pharmacist sees real prices on first look instead of ₹0. The
  // doctor's Rx carries name + qty + uom but rarely price/GST — so the
  // seed row arrives with price=0 and gst_percent=12. Previously this
  // stayed stale until the pharmacist manually re-picked the medicine
  // from the typeahead suggestion box.
  //
  // Order of preference for each row:
  //   1. Tenant's own `medicines_lite` (exact then loose match) —
  //      guarantees the batch/UOM/GST the clinic actually stocks.
  //   2. Shared `medicines_catalog` (246K Indian meds, all tenants) via
  //      `/api/common/medicines?search=` — MRP only; uses a smart query
  //      builder to strip the "generic (brand) strength" wrapper the
  //      doctor's Rx uses.
  // Rows the pharmacist has already touched — any row with a non-zero
  // price — are left untouched.
  useEffect(() => {
    if (hydrated) return;
    // Wait for the inventory fetch to settle before we start looking
    // up prices; otherwise we'd fire the catalog fallback for every
    // row even when the local inventory already has them.
    if (inventory === null) return;

    let cancelled = false;
    (async () => {
      // Snapshot the current items so we don't clobber the pharmacist's
      // typing if they've already started editing during the effect.
      const start = items;
      const findLocal = (nameLc) => {
        if (!inventory.length) return null;
        let m = inventory.find(mm => (mm.name || '').trim().toLowerCase() === nameLc);
        if (m) return m;
        return inventory.find(mm => {
          const mn = (mm.name || '').toLowerCase();
          return mn && (mn.includes(nameLc) || nameLc.includes(mn));
        }) || null;
      };
      // Batch-fetch catalog for every row that lacks a local hit.
      // For each row we may fire multiple progressively broader
      // queries (in serial) until one returns a hit — see
      // buildCatalogQueries for the fallback ladder.
      const fetchCatalog = async (rxName) => {
        const queries = buildCatalogQueries(rxName);
        for (const q of queries) {
          if (!q || q.length < 2) continue;
          try {
            const { data } = await ax.get('/api/common/medicines', { params: { search: q } });
            if (Array.isArray(data) && data.length > 0) return data[0];
          } catch { /* try next */ }
        }
        return null;
      };
      const catalogLookups = start.map((it) => {
        if (Number(it.price) > 0) return Promise.resolve(null);
        const nameLc = (it.name || '').trim().toLowerCase();
        if (!nameLc) return Promise.resolve(null);
        if (findLocal(nameLc)) return Promise.resolve(null); // local wins
        return fetchCatalog(it.name);
      });
      const catalogResults = await Promise.all(catalogLookups);
      if (cancelled) return;

      const nextItems = start.map((it, i) => {
        if (Number(it.price) > 0) return it;
        const nameLc = (it.name || '').trim().toLowerCase();
        if (!nameLc) return it;
        const local = findLocal(nameLc);
        if (local) {
          return {
            ...it,
            price: local.mrp || 0,
            gst_percent: local.gst_percent ?? it.gst_percent,
            uom: local.uom || it.uom,
          };
        }
        const cat = catalogResults[i];
        if (!cat) return it;
        // Map the catalog's freeform pack description ("strip of 30 tablets")
        // to one of our canonical UOM codes. Only override if we can
        // confidently classify — otherwise keep the seed UOM ("tab") so
        // the Select doesn't lose its selected value.
        const catUom = String(cat.subCategory || cat.type || '').toLowerCase();
        const knownUoms = ['tab', 'cap', 'ml', 'syrup', 'drop', 'vial', 'amp', 'strip', 'bottle', 'sachet', 'unit'];
        let mappedUom = it.uom || 'tab';
        if (/tablet/.test(catUom)) mappedUom = 'tab';
        else if (/capsule/.test(catUom)) mappedUom = 'cap';
        else if (/syrup|suspension/.test(catUom)) mappedUom = 'syrup';
        else if (/drop/.test(catUom)) mappedUom = 'drop';
        else if (/vial/.test(catUom)) mappedUom = 'vial';
        else if (/ampoule|ampule|\bamp\b/.test(catUom)) mappedUom = 'amp';
        else if (/sachet/.test(catUom)) mappedUom = 'sachet';
        else if (/bottle/.test(catUom)) mappedUom = 'bottle';
        else if (/strip/.test(catUom)) mappedUom = 'tab'; // "strip of 30 tablets" → tab
        else if (knownUoms.includes(catUom)) mappedUom = catUom;
        return {
          ...it,
          price: Number(cat.mrp) || 0,
          uom: mappedUom,
          // Catalog rows carry no GST — leave the default (12%) so the
          // pharmacist can bump it if the medicine is in a different slab.
        };
      });
      setItems(nextItems);
      // Refresh the audit baseline so an auto-hydrated row doesn't
      // register as a pharmacist edit (would otherwise force the
      // "edit reason" dropdown to appear even for an unchanged Rx).
      setOriginalSnapshot(nextItems.map(x => ({ ...x })));
      setHydrated(true);
    })();
    return () => { cancelled = true; };
  }, [inventory, hydrated]);

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const gst = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0) * (Number(it.gst_percent ?? 12) / 100), 0);
  const cgst = gst / 2;
  const sgst = gst - cgst;
  const grandTotal = subtotal + gst;

  const updateItem = (i, patch) => {
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  };
  const pickInventory = (i, med) => {
    // Substituting a medicine: also auto-pull rate + GST + UOM from
    // inventory so the pharmacist doesn't have to retype.
    updateItem(i, {
      name: med.name,
      uom: med.uom || 'tab',
      price: med.mrp || 0,
      gst_percent: med.gst_percent ?? 12,
    });
    setSuggestFor(null);
  };
  const addItem = () => setItems([...items, { name: '', qty: 1, uom: 'tab', price: 0, gst_percent: 12 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // Has anything changed vs the doctor's Rx? Drives the "Edit reason"
  // optional dropdown so the pharmacist can tag the audit with context.
  const hasEdits = JSON.stringify(items.map(({ name, qty, uom, price, gst_percent }) =>
    ({ name: (name || '').trim().toLowerCase(), qty: Number(qty) || 0, uom: uom || '', price: Number(price) || 0, gst_percent: Number(gst_percent ?? 12) })
  )) !== JSON.stringify(originalSnapshot.map(({ name, qty, uom, price, gst_percent }) =>
    ({ name: (name || '').trim().toLowerCase(), qty: Number(qty) || 0, uom: uom || '', price: Number(price) || 0, gst_percent: Number(gst_percent ?? 12) })
  ));

  const matchInventory = (q) => {
    const list = inventory || [];
    if (!q) return list.slice(0, 6);
    const lq = q.toLowerCase();
    return list.filter(m => (m.name || '').toLowerCase().includes(lq)).slice(0, 6);
  };

  const dispense = async () => {
    const clean = items.filter(it => it.name?.trim());
    if (clean.length === 0) return toast.error('Add at least one item');
    setSubmitting(true);
    try {
      const { data } = await ax.post('/api/clinic-ops/dispense', {
        prescription_id: selected.consultation_id,
        patient_id: selected.patient_id,
        patient_name: selected.patient_name,
        original_prescription: originalSnapshot,
        edit_reason: hasEdits ? (editReason || '') : '',
        items: clean.map(it => ({
          name: it.name.trim(),
          qty: Number(it.qty) || 1,
          uom: (it.uom || 'tab'),
          price: Number(it.price) || 0,
          gst_percent: Number(it.gst_percent ?? 12),
        })),
        payment_amount: parseFloat(amount || 0) || grandTotal,
        payment_method: method,
        payment_reference: reference,
      });
      toast.success('Dispensed & collected ₹' + (parseFloat(amount || 0) || grandTotal).toFixed(2));
      onReceipt?.(data);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Dispense failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="pl-dispense-form">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-zinc-900 text-lg">{selected.patient_name}</h3>
          <p className="text-xs text-zinc-500">Prescription · {selected.consultation_id}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel}>Change</Button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm">Items</Label>
          <Button variant="ghost" size="sm" onClick={addItem} data-testid="pl-add-item">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add line
          </Button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => {
            const original = originalSnapshot[i];
            const rowChanged = original && (
              (original.name || '').trim().toLowerCase() !== (it.name || '').trim().toLowerCase()
              || Number(original.qty) !== Number(it.qty)
              || (original.uom || '') !== (it.uom || '')
              || Number(original.price) !== Number(it.price)
              || Number(original.gst_percent ?? 12) !== Number(it.gst_percent ?? 12)
            );
            const newRow = !original;
            const suggestions = matchInventory(it.name);
            return (
              <div key={i} className={`relative grid grid-cols-[1fr_56px_72px_84px_70px_36px] gap-2 items-center p-2 rounded-md border ${
                newRow ? 'border-emerald-200 bg-emerald-50/40'
                : rowChanged ? 'border-amber-200 bg-amber-50/40'
                : 'border-transparent'}`}
                data-testid={`pl-item-row-${i}`}>
                <div className="relative">
                  <Input value={it.name}
                    onChange={(e) => updateItem(i, { name: e.target.value })}
                    onFocus={() => setSuggestFor(i)}
                    onBlur={() => setTimeout(() => setSuggestFor(null), 150)}
                    placeholder="Medicine name" data-testid={`pl-item-name-${i}`} />
                  {suggestFor === i && suggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-30 max-h-44 overflow-y-auto"
                         data-testid={`pl-item-suggestions-${i}`}>
                      {suggestions.map((m) => (
                        <button key={m.id} type="button"
                          onMouseDown={(e) => { e.preventDefault(); pickInventory(i, m); }}
                          className="block w-full text-left px-2.5 py-1.5 text-xs hover:bg-emerald-50">
                          <div className="font-semibold text-zinc-900">{m.name}</div>
                          <div className="text-[10px] text-zinc-500">
                            Stock {m.quantity}{m.uom ? ` ${m.uom}` : ''} · ₹{m.mrp} · GST {m.gst_percent ?? 12}%
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {(rowChanged || newRow) && (
                    <span className={`absolute -top-2 left-1 px-1 text-[8px] uppercase tracking-wider font-bold rounded ${
                      newRow ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'
                    }`}>{newRow ? 'added' : 'edited'}</span>
                  )}
                </div>
                <Input type="number" min="0" step="0.5" value={it.qty}
                  onChange={(e) => updateItem(i, { qty: e.target.value })}
                  placeholder="Qty" data-testid={`pl-item-qty-${i}`} />
                <Select value={it.uom || 'tab'}
                  onValueChange={(v) => updateItem(i, { uom: v })}>
                  <SelectTrigger data-testid={`pl-item-uom-${i}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tab">Tab</SelectItem>
                    <SelectItem value="cap">Cap</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="syrup">Syrup</SelectItem>
                    <SelectItem value="drop">Drops</SelectItem>
                    <SelectItem value="vial">Vial</SelectItem>
                    <SelectItem value="amp">Amp</SelectItem>
                    <SelectItem value="strip">Strip</SelectItem>
                    <SelectItem value="bottle">Bottle</SelectItem>
                    <SelectItem value="sachet">Sachet</SelectItem>
                    <SelectItem value="unit">Unit</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" min="0" step="0.01" value={it.price}
                  onChange={(e) => updateItem(i, { price: e.target.value })}
                  placeholder="Rate" data-testid={`pl-item-price-${i}`} />
                <Select value={String(it.gst_percent ?? 12)}
                  onValueChange={(v) => updateItem(i, { gst_percent: Number(v) })}>
                  <SelectTrigger data-testid={`pl-item-gst-${i}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => removeItem(i)}
                  data-testid={`pl-item-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Audit reason — only shown when the pharmacist actually edited the Rx */}
      {hasEdits && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2.5"
             data-testid="pl-audit-reason-wrap">
          <Label className="text-[11px] uppercase tracking-wider font-bold text-amber-700">
            Why was the Rx edited? (logged for audit)
          </Label>
          <Select value={editReason} onValueChange={setEditReason}>
            <SelectTrigger className="h-9 text-xs mt-1" data-testid="pl-audit-reason">
              <SelectValue placeholder="Pick a reason (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="stock_out">Out of stock — substituted</SelectItem>
              <SelectItem value="patient_request">Patient preference</SelectItem>
              <SelectItem value="doctor_approved">Doctor verbally approved</SelectItem>
              <SelectItem value="price_correction">Price/GST correction</SelectItem>
              <SelectItem value="strength_swap">Strength / formulation swap</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Live GST breakdown — same math the backend will apply. */}
      <div className="bg-zinc-50 border rounded-lg px-3 py-2 text-xs space-y-1"
           data-testid="pl-gst-summary">
        <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-semibold">₹{subtotal.toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">CGST</span><span className="font-semibold">₹{cgst.toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">SGST</span><span className="font-semibold">₹{sgst.toFixed(2)}</span></div>
        <div className="flex justify-between border-t pt-1 mt-1 font-bold text-zinc-900 text-sm"><span>Grand Total</span><span>₹{grandTotal.toFixed(2)}</span></div>
      </div>

      <div className="border-t pt-3 grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <IndianRupee className="h-3 w-3" /> Amount to collect
          </Label>
          <Input type="number" min="0" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Default ₹${grandTotal.toFixed(2)}`} data-testid="pl-amount" />
          <p className="text-[10px] text-zinc-500">Override only for manual discounts.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Payment method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="pl-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="netbanking">Net Banking</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {method !== 'cash' && (
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Reference (last 4 / UPI txn id)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. TXN/9876" data-testid="pl-payref" />
          </div>
        )}
      </div>

      <Button onClick={dispense} disabled={submitting}
        className="w-full bg-emerald-700 hover:bg-emerald-800"
        data-testid="pl-dispense-submit">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
          : <CheckCircle2 className="h-4 w-4 mr-2" />}
        Dispense &amp; collect ₹{(parseFloat(amount || 0) || grandTotal).toFixed(2)}
      </Button>
    </div>
  );
}
